import { Component, Input, OnInit, inject, signal, effect, OnDestroy, output, computed, OnChanges, SimpleChanges, input } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormGroup, FormArray, FormBuilder, ReactiveFormsModule, Validators, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { AnaliteService } from '@servicios/analite/analite.service';
import { ConfiguracionesTecnicasService } from '@servicios/configuracionesTecnicas.service';
import { TechConfigModels } from '@interfaces/operation-result-types';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { LoadOptionsService } from '@servicios/load_options/load_options.service';
import { forkJoin, Subscription } from 'rxjs';
import { SampleAssaysService } from '@servicios/samples/samples-assays.service';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { StudyService } from '@servicios/inventory/study.service';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { PopoverModule } from 'primeng/popover';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'app-test-item-bpl-data',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    SelectModule,
    AutoCompleteModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    TextareaModule,
    TooltipModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    PopoverModule,
    MessageModule
  ],
  templateUrl: './test-item-bpl-data.component.html',
  styleUrls: ['./test-item-bpl-data.component.scss']
})
export class TestItemBplDataComponent implements OnInit, OnDestroy, OnChanges {
  @Input() parentForm!: FormGroup;
  @Input() item: any = null;

  onPrintPartition = output<number>();

  private fb = inject(FormBuilder);
  private analiteService = inject(AnaliteService);
  private techConfigService = inject(ConfiguracionesTecnicasService);
  private opts = inject(LoadOptionsService);
  private confirmationService = inject(ConfirmationService);
  private studyService = inject(StudyService);
  private inventoryService = inject(InventoryService);
  private messageService = inject(MessageService);

  // Computed properties for Mass Balance Summary
  totalConsumed = signal<number>(0);
  receivedQuantity = signal<number>(0);
  fractionsOverflow = signal<boolean>(false);
  fractionsOverflowMsg = signal<string>('');
  theoreticalRemaining = computed(() => {
    return this.receivedQuantity() - this.totalConsumed();
  });
  private sub: Subscription | null = null;

  // Master Data
  units = signal<any[]>([]);
  filteredAnalites = signal<any[]>([]);
  customers = signal<{ id: string, code: string, name: string, tradename: string }[]>([]);
  studies = signal<{ label: string, value: string }[]>([]);
  studyTags = signal<{ label: string, value: string }[]>([]);
  matrices = signal<{ label: string, value: string }[]>([]);

  createStudyDialogVisible = false;
  selectedStudyTagId: string | null = null;
  generatingStudy = false;

  customersOptsSG = {
    name: 'customersOptsSG',
    filteredValues: this.customers
  };

  // Arrays from Parent
  formulationTypes = input<any[]>([]);

  toxicologyClasses = [
    { label: 'I (Extremadamente Peligroso)', value: 'I' },
    { label: 'II (Altamente Peligroso)', value: 'II' },
    { label: 'III (Moderadamente Peligroso)', value: 'III' },
    { label: 'IV (Ligeramente Peligroso)', value: 'IV' },
    { label: 'U (Improbable que presente peligro)', value: 'U' }
  ];

  physicalStates = [
    { label: 'Líquido', value: 'LIQUID' },
    { label: 'Sólido', value: 'SOLID' }
  ];

  private subs = new Subscription();

  constructor() {}

  ngOnInit() {
    this.ensureCompositionArray();
    this.ensureFractionsArray();
    this.loadUnits();
    this.loadCustomers();
    this.loadStudies();
    this.loadMatrices();
    this.updateDisabledState();

    const initReceived = +(this.parentForm.get('received_quantity')?.value || this.item?.received_quantity || 0);
    this.receivedQuantity.set(initReceived);

    // Re-calculate theoretical remaining if received quantity changes dynamically
    const rqSub = this.parentForm.get('received_quantity')?.valueChanges.subscribe(val => {
      this.receivedQuantity.set(+(val || 0));
      this.calculateTotalConsumed();
    });
    if (rqSub) this.subs.add(rqSub);

    const frSub = this.parentForm.get('fractions')?.valueChanges.subscribe(() => {
      this.calculateTotalConsumed();
    });
    if (frSub) this.subs.add(frSub);

    let previousFormulationType = this.parentForm.get('formulationTypeId')?.value;

    const ftSub = this.parentForm.get('formulationTypeId')?.valueChanges.subscribe(val => {
      if (val === previousFormulationType) return;

      const checkMultipleActives = (formulation: string) => {
        const selectedFormulation = this.formulationTypes().flatMap(g => g.items).find(ft => ft.value === formulation)?.original;
        const allowMultiple = selectedFormulation ? selectedFormulation.allow_multiple_actives : true;

        if (!allowMultiple && this.compositionArray.length > 1) {
          this.confirmationService.confirm({
            message: 'Este tipo de formulación solo permite 1 principio activo. Si continuás, se eliminarán los principios activos adicionales. ¿Estás seguro?',
            header: 'Confirmar Cambio',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, cambiar y eliminar',
            rejectLabel: 'Cancelar',
            accept: () => {
              while (this.compositionArray.length > 1) {
                this.compositionArray.removeAt(this.compositionArray.length - 1);
              }
              previousFormulationType = formulation;
            },
            reject: () => {
              this.parentForm.get('formulationTypeId')?.setValue(previousFormulationType, { emitEvent: false });
            }
          });
        } else {
          previousFormulationType = formulation;
        }
      };

      // Check if item has a study and formulation changed
      if (this.item?.studyId && previousFormulationType) {
        this.confirmationService.confirm({
          message: 'Atención: Al cambiar el tipo de formulación, el código del estudio asociado quedará desactualizado/incoherente con la formulación real del ítem. ¿Desea continuar de todas formas?',
          header: 'Advertencia de Coherencia (BPL)',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Sí, continuar',
          rejectLabel: 'Cancelar',
          accept: () => {
            checkMultipleActives(val);
          },
          reject: () => {
            this.parentForm.get('formulationTypeId')?.patchValue(previousFormulationType, { emitEvent: false });
          }
        });
      } else {
        checkMultipleActives(val);
      }
    });
    if (ftSub) this.subs.add(ftSub);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['item'] && changes['item'].currentValue) {
      const rec = +(this.parentForm.get('received_quantity')?.value || this.item?.received_quantity || 0);
      this.receivedQuantity.set(rec);
      this.calculateTotalConsumed();
      this.updateDisabledState();
    }
  }

  onReceivedQuantityInput(event: any) {
    const val = event?.value !== undefined && event?.value !== null ? +event.value : 0;
    this.receivedQuantity.set(val);
    this.calculateTotalConsumed();
  }

  updateDisabledState() {
    if (this.item?.mass_balance_status === 'CLOSED') {
      this.parentForm.get('received_quantity')?.disable({ emitEvent: false });
      this.parentForm.get('experimental_remaining')?.disable({ emitEvent: false });
      this.parentForm.get('observations')?.disable({ emitEvent: false });
      this.parentForm.get('received_unit_id')?.disable({ emitEvent: false });

      const fractions = this.parentForm.get('fractions') as FormArray;
      if (fractions) {
        for (let i = 0; i < fractions.length; i++) {
          fractions.at(i).get('tare')?.disable({ emitEvent: false });
          fractions.at(i).get('initial_mass')?.disable({ emitEvent: false });
          fractions.at(i).get('name')?.disable({ emitEvent: false });
        }
      }
    }
  }

  calculateTotalConsumed() {
    let total = 0;
    const fractionsArray = this.parentForm.get('fractions') as FormArray;
    const fractions = fractionsArray ? fractionsArray.getRawValue() : [];
    fractions.forEach((f: any) => {
      total += +(f.initial_mass || 0);
    });
    this.totalConsumed.set(total);

    const receivedQty = this.receivedQuantity();
    if (receivedQty > 0) {
      this.fractionsOverflow.set(total > receivedQty);
      if (total > receivedQty) {
        this.fractionsOverflowMsg.set(`La suma del peso de las particiones (${total.toFixed(2)} g) supera la cantidad recibida (${receivedQty.toFixed(2)} g). Revisar los valores ingresados.`);
      }
    } else {
      this.fractionsOverflow.set(false);
    }
  }

  getMaxMassForPartition(index: number): number {
    const received = +(this.parentForm.get('received_quantity')?.value || 0);
    const fractions = this.parentForm.get('fractions')?.getRawValue() || [];
    let othersTotal = 0;
    fractions.forEach((f: any, i: number) => {
      if (i !== index) {
        othersTotal += +(f.initial_mass || 0);
      }
    });

    // We add a tiny buffer (0.0001) to prevent floating point rounding issues from blocking valid inputs
    const maxVal = Math.max(0, received - othersTotal);
    return parseFloat((maxVal + 0.0001).toFixed(4));
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.subs.unsubscribe();
  }

  // Getters
  get compositionArray(): FormArray {
    return this.parentForm.get('composition') as FormArray;
  }

  get fractionsArray(): FormArray {
    return this.parentForm.get('fractions') as FormArray;
  }

  get allowsMultipleActives(): boolean {
    const val = this.parentForm.get('formulationTypeId')?.value;
    const selectedFormulation = this.formulationTypes().flatMap(g => g.items).find(ft => ft.value === val)?.original;
    return selectedFormulation ? selectedFormulation.allow_multiple_actives : true;
  }

  private ensureCompositionArray() {
    if (!this.parentForm.get('composition')) {
      this.parentForm.addControl('composition', this.fb.array([]));
    }
  }

  private ensureFractionsArray() {
    if (!this.parentForm.get('fractions')) {
      this.parentForm.addControl('fractions', this.fb.array([]));
    }
  }

  async loadUnits() {
    try {
      const unitsRes = await firstValueFrom(this.techConfigService._getAll(TechConfigModels.units));
      if (unitsRes?.data?.techConfigs) {
        const mappedUnits = unitsRes.data.techConfigs.map(u => ({
          label: u.translation?.['es'] || u.value || u.abbreviation,
          value: u.id
        }));
        this.units.set(mappedUnits);

        // Set default unit to 'g' if none is currently selected
        const currentUnitId = this.parentForm.get('received_unit_id')?.value;
        if (!currentUnitId && mappedUnits.length > 0) {
          const gUnit = mappedUnits.find(u => {
            const lbl = u.label.toLowerCase().trim();
            return lbl === 'g' || lbl === 'gr' || lbl === 'gramos';
          });
          if (gUnit) {
            this.parentForm.patchValue({ received_unit_id: gUnit.value }, { emitEvent: false });
          }
        }
      }
    } catch (e) {
      console.error('Error loading units', e);
    }
  }

  async loadMatrices() {
    try {
      const res: any = await firstValueFrom(this.techConfigService._getAll(TechConfigModels.matrix));
      if (res?.data?.techConfigs) {
        this.matrices.set(res.data.techConfigs.map((m: any) => ({
          label: `${m.code ? m.code + ' - ' : ''}${m.value || m.name}`,
          value: m.id
        })));
      }
    } catch (e) {
      console.error('Error loading matrices', e);
    }
  }

  loadCustomers() {
    const optsToLoad = this.opts.loadOptions([this.customersOptsSG], true);
    if (optsToLoad.length > 0) {
      const custSub = forkJoin(optsToLoad).subscribe({
        error: (e) => console.error('Error loading customers options', e),
      });
      this.subs.add(custSub);
    }
  }

  async loadStudies() {
    try {
      const res = await firstValueFrom(this.studyService.getAll());
      if (res && Array.isArray(res)) {
        this.studies.set(res.map((s: any) => ({
          label: s.code + (s.description ? ` - ${s.description}` : ''),
          value: s.id
        })));
      }
    } catch (e) {
      console.error('Error loading studies', e);
    }
  }

  async loadStudyTags() {
    try {
      const res = await this.inventoryService.getAll('study-tags', { limit: 1000 });
      if (res?.data) {
        this.studyTags.set(res.data.map((t: any) => ({
          label: t.name,
          value: t.id
        })));
      }
    } catch (e) {
      console.error('Error loading study tags', e);
    }
  }

  showCreateStudyDialog() {
    if (!this.studyTags().length) {
      this.loadStudyTags();
    }
    this.selectedStudyTagId = null;
    this.createStudyDialogVisible = true;
  }

  isStudyButtonDisabled(): boolean {
    if (!this.item?.id) return true;
    if (this.parentForm.disabled) return true;
    if (this.parentForm.dirty) return true;
    if (!this.parentForm.get('formulationTypeId')?.value) return true;

    const composition = this.parentForm.get('composition') as FormArray;
    if (!composition || composition.controls.length === 0) return true;

    return false;
  }

  getStudyButtonTooltip(): string {
    if (!this.item?.id) return 'Debe guardar el ítem primero';
    if (this.parentForm.dirty) return 'Hay cambios sin guardar. Guarde el ítem antes de generar el estudio.';
    if (!this.parentForm.get('formulationTypeId')?.value) return 'Debe seleccionar un tipo de formulación';

    const composition = this.parentForm.get('composition') as FormArray;
    if (!composition || composition.controls.length === 0) return 'Debe agregar al menos un principio activo';

    return 'Crear Nuevo Estudio';
  }

  async generateStudy() {
    try {
      this.generatingStudy = true;
      const itemId = this.parentForm.get('id')?.value || this.item?.id;
      if (!itemId) {
        throw new Error('Debe guardar el ítem primero para poder generar un estudio.');
      }
      const newStudy = await firstValueFrom(this.studyService.generate(itemId, this.selectedStudyTagId || undefined));

      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `Estudio ${newStudy.code} generado correctamente` });
      this.createStudyDialogVisible = false;

      await this.loadStudies();
      this.parentForm.patchValue({ studyId: newStudy.id }, { emitEvent: false });

    } catch (e: any) {
      console.error('Error generating study', e);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: e.error?.message || e.message || 'No se pudo generar el estudio' });
    } finally {
      this.generatingStudy = false;
    }
  }

  async searchAnalites(event: any) {
    try {
      const query = event.query;
      const res = await this.analiteService.getPaginated({
        page: 1,
        limit: 20,
        search: query
      });
      this.filteredAnalites.set(res.response.map((a: any) => {
        const code = a.code || a.abbreviation || a.analite_code || '';
        return {
          ...a,
          code,
          analite_code: code,
          label: `${code ? '[' + code + '] ' : ''}${a.name}`
        };
      }));
    } catch (e) {
      console.error('Error searching analites', e);
    }
  }

  analiteValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null; // Let Validators.required handle empty
    if (typeof value !== 'object' || !value.id) {
      return { invalidAnalite: true };
    }
    return null;
  };

  addActiveIngredient() {
    if (!this.allowsMultipleActives && this.compositionArray.length >= 1) {
      return; // Validation: Items that do not allow multiple actives can only have 1 active ingredient
    }

    const group = this.fb.group({
      analite: [null, [Validators.required, this.analiteValidator]],
      concentration: [0, [Validators.required, Validators.min(0)]],
      unit_id: [null, Validators.required],
      order: [this.compositionArray.length + 1]
    });
    this.compositionArray.push(group);
  }

  removeActiveIngredient(index: number) {
    this.compositionArray.removeAt(index);
    // Reorder
    this.compositionArray.controls.forEach((control, idx) => {
      control.get('order')?.setValue(idx + 1);
    });
  }

  addPartition() {
    const parentCode = this.parentForm.get('internal_code')?.value || 'PART';
    const index = this.fractionsArray.length + 1;
    const group = this.fb.group({
      code: [{ value: `${parentCode}-${index}`, disabled: true }, Validators.required],
      name: ['', Validators.required],
      destination: [''],
      tare: [null, [Validators.required, Validators.min(0)]],
      initial_mass: [null, [Validators.required, Validators.min(0)]],
      consumptions: [[]],
      theoretical_rem: [null],
      experimental_rem: [null],
      difference: [null]
    });

    const nameSub = group.get('name')?.valueChanges.subscribe(val => {
      group.get('destination')?.setValue(val || '', { emitEvent: false });
    });
    if (nameSub) this.subs.add(nameSub);

    const massSub = group.get('initial_mass')?.valueChanges.subscribe(val => {
      const consumptions = group.get('consumptions')?.value || [];
      const totalConsumed = consumptions.reduce((sum: number, c: any) => sum + (c.mass || 0), 0);
      group.get('theoretical_rem')?.setValue((val || 0) - totalConsumed, { emitEvent: false });
    });
    if (massSub) this.subs.add(massSub);

    this.fractionsArray.push(group);
  }

  removePartition(index: number) {
    this.fractionsArray.removeAt(index);
    // Reorder/Re-code remaining partitions
    const parentCode = this.parentForm.get('internal_code')?.value || 'PART';
    this.fractionsArray.controls.forEach((control, idx) => {
      control.get('code')?.setValue(`${parentCode}-${idx + 1}`);
    });
  }

  triggerPrintPartition(index: number) {
    this.onPrintPartition.emit(index);
  }
}
